import { StepLocationRelativeToParent } from '@activepieces/shared';
import {
  BaseEdge,
  EdgeProps,
  InternalNode,
  Position,
  useInternalNode,
} from '@xyflow/react';

import { flowUtilConsts } from '../utils/consts';
import { ApStraightLineEdge } from '../utils/types';

import { ApAddButton } from './add-button';
import { ApDetachButton } from './detach-button';

function getNodeIntersection(
  intersectionNode: InternalNode,
  targetNode: InternalNode
) {
  const { width: nodeWidth = 0, height: nodeHeight = 0 } =
    intersectionNode.measured || {};
  const { x: nodeX, y: nodeY } = intersectionNode.internals.positionAbsolute;
  const { x: targetX, y: targetY } = targetNode.internals.positionAbsolute;
  const cx = nodeX + nodeWidth / 2;
  const cy = nodeY + nodeHeight / 2;
  const targetCenter = {
    x: targetX + (targetNode.measured?.width ?? 0) / 2,
    y: targetY + (targetNode.measured?.height ?? 0) / 2,
  };
  const dx = targetCenter.x - cx;
  const dy = targetCenter.y - cy;
  const d = Math.sqrt(dx * dx + dy * dy) || 1;
  const r = (nodeWidth + nodeHeight) / 4;
  return {
    x: cx + (r * dx) / d,
    y: cy + (r * dy) / d,
  };
}
function getEdgePosition(
  node: InternalNode,
  intersectionPoint: { x: number; y: number }
): Position {
  const { x: nx, y: ny } = node.internals.positionAbsolute;
  const { width = 0, height = 0 } = node.measured || {};
  const { x: px, y: py } = intersectionPoint;

  if (px <= nx + 1) return Position.Left;
  if (px >= nx + width - 1) return Position.Right;
  if (py <= ny + 1) return Position.Top;
  if (py >= ny + height - 1) return Position.Bottom;

  return Position.Top;
}

export function getEdgeParams(source: InternalNode, target: InternalNode) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}

export const ApStraightLineCanvasEdge = ({
  sourceX,
  sourceY,
  targetY,
  data,
  id,
  source,
  target,
}: EdgeProps & ApStraightLineEdge) => {
  const lineStartX = sourceX;
  const lineStartY =
    sourceY + flowUtilConsts.VERTICAL_SPACE_BETWEEN_STEP_AND_LINE;
  const showDebugForLineEndPoint = false;

  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;
  const { sx, sy, tx, ty } = getEdgeParams(sourceNode, targetNode);
  const path = `M ${sx},${sy} L ${tx},${ty}`;
  const centerX = (sx + tx) / 2;
  const centerY = (sy + ty) / 2;
  const containerWidth = flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.width * 2 + 10;
  const containerHeight = flowUtilConsts.AP_NODE_SIZE.ADD_BUTTON.height;
  return (
    <g>
      <BaseEdge
        path={path}
        className="animated-edge"
        style={{
          strokeWidth: `${flowUtilConsts.LINE_WIDTH}px`,
          stroke: data.hover ? '#ff0000' : '#999999',
          transition: 'stroke 0.2s ease',
        }}
      />
      {!data.hideButtons && data.hover && (
        <foreignObject
          x={centerX - containerWidth / 2}
          y={centerY - containerHeight / 2}
          width={containerWidth}
          height={containerHeight}
          className="overflow-visible cursor-default"
        >
          <div className="flex flex-row gap-[10px] justify-around items-center w-full h-full">
            <ApAddButton
              edgeId={id}
              parentStepName={source}
              stepLocationRelativeToParent={StepLocationRelativeToParent.AFTER}
              connection={{ sourceId: source, targetId: target }}
              newStepPosition={{ x: centerX, y: centerY }}
            />
            <ApDetachButton
              edgeId={id}
              parentStepName={source}
              stepLocationRelativeToParent={StepLocationRelativeToParent.AFTER}
              connection={{ sourceId: source, targetId: target }}
            />
          </div>
        </foreignObject>
      )}

      {showDebugForLineEndPoint && (
        <foreignObject
          x={lineStartX}
          y={lineStartY + targetY - sourceY}
          className="w-[20px] h-[20px] rounded-full bg-[red] flex items-center justify-center absolute"
        >
          <div className=" w-[20px] h-[20px] rounded-full bg-[red] flex items-center justify-center"></div>
        </foreignObject>
      )}
    </g>
  );
};
